import type { NextPage } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { AboutContextProvider } from '@/contexts/AboutContext'
import { Seo } from '@/layout/Seo'
import { GUILD_MEMBERSHIP_NUMBERS_ID, OUR_IMPACT_ENTRY_ID, getCollection } from '@/services/CmsService/Collection'
import { GET_PROJECT_TILES } from '@/services/ProjectsService'
import { Collection } from '@/types/codegen-contentful'
import { GetProjectTilesQuery } from '@/types/codegen-federation'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { AboutView } from '@/views/AboutView'

interface AboutProps {
  impact: Collection
  locale: string
  preview: boolean
  tiles: GetProjectTilesQuery
  members: string | null
  lastUpdated: string | null
  guildMembershipNumbers: Collection
}

const Home: NextPage<AboutProps> = ({ impact, locale, tiles, members, lastUpdated, guildMembershipNumbers }) => {
  const { t } = useTranslate('about')
  const url = `${getBasePath(locale)}`
  const title = t('angelAboutTitle', 'About Angel | Angel')
  const description = t(
    'angelAboutDescription',
    'Angel is a leading crowd-funded streaming service and film studio whose mission is to create stories that amplify the light. Learn more about us here.',
  )

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.about.index}
        title={title}
      />
      <AboutContextProvider
        impact={impact}
        tiles={tiles}
        numberOfGuildMembers={members}
        lastUpdated={lastUpdated}
        guildMembershipNumbers={guildMembershipNumbers}
      >
        <AboutView />
      </AboutContextProvider>
    </>
  )
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<AboutProps>(
  { filePath: 'pages/[locale]/about/index.tsx' },
  async ({ federationClient, locale, preview, next404 }) => {
    const [impact, guildMembershipNumbers, tiles, translations] = await Promise.all([
      getCollection({ locale, preview }, OUR_IMPACT_ENTRY_ID),
      getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID),
      federationClient.query<GetProjectTilesQuery>({
        query: GET_PROJECT_TILES,
        errorPolicy: 'all',
      }),
      loadTranslations(locale, ['common', 'about', 'app-promo', 'account']),
    ])

    if (!impact || !guildMembershipNumbers) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    const members = guildMembershipNumbers.customData[0]?.numSubscribers ?? null
    const lastUpdated = guildMembershipNumbers.customData[0]?.updatedAt ?? null

    return {
      props: {
        impact,
        locale,
        preview,
        tiles: tiles.data,
        members,
        lastUpdated,
        guildMembershipNumbers,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default Home
